import React from 'react';
import DeluxeVaultSetup from '../../deluxe/components/DeluxeVaultSetup';
import '../styles/custom-plan-invite.css';

function CheckIcon() {
  return (
    <svg className="cpi-summary__check" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function PasswordMeter({ score, label, strengthClass }) {
  return (
    <div className="cpi-pw-meter">
      <div className="cpi-pw-meter__bars">
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className={`cpi-pw-meter__bar ${score > index ? `is-active ${strengthClass}` : ''}`}
          />
        ))}
      </div>
      <div className="cpi-pw-meter__label">{label}</div>
    </div>
  );
}

export default function CustomPlanInviteAcceptView({
  plan,
  totals,
  billingLabel,
  email,
  firstName,
  lastName,
  companyName,
  password,
  confirmPassword,
  billingAddress,
  showPassword,
  showConfirmPassword,
  passwordScore,
  passwordLabel,
  passwordStrengthClass,
  error,
  allowTestCards = false,
  submitDisabled = false,
  isPreview = false,
  onFirstNameChange,
  onLastNameChange,
  onCompanyNameChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onTogglePassword,
  onToggleConfirmPassword,
  onBillingChange,
  onPaymentChange,
  payment,
  onAccept,
  onCancel,
}) {
  const features = plan?.terms?.features || [];
  const expiresAt = plan?.expiresAt
    ? new Date(plan.expiresAt).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className={`cpi-page${isPreview ? ' cpi-page--preview' : ''}`}>
      {isPreview ? (
        <div className="cpi-preview-banner" role="status">
          Preview mode — mock data only, no billing or account changes
        </div>
      ) : null}

      <div className="cpi-shell">
        <div className="cpi-layout">
          <aside className="cpi-summary" aria-label="Plan summary">
            <div className="cpi-summary__brand">
              <img src="/neatschedule.png" alt="" className="cpi-summary__logo" />
              <p className="cpi-summary__eyebrow">Custom plan</p>
              <h2 className="cpi-summary__title">{plan.terms.planName}</h2>
              {plan.customerLabel ? (
                <p className="cpi-summary__customer">Prepared for {plan.customerLabel}</p>
              ) : null}
            </div>

            <div className="cpi-summary__price-block">
              <p className="cpi-summary__price">
                ${totals.subtotal.toFixed(2)}
                <span> / {billingLabel}</span>
              </p>
              <p className="cpi-summary__price-note">
                Plus applicable tax · Billed {plan.terms.billingCycle === 'annually' ? 'annually' : 'monthly'}
              </p>
            </div>

            {features.length > 0 ? (
              <div className="cpi-summary__features">
                <h2>What&apos;s included</h2>
                <ul className="cpi-summary__feature-list">
                  {features.map((feature) => (
                    <li key={feature}>
                      <CheckIcon />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.terms.customerNotes ? (
                  <div className="cpi-summary__notes">{plan.terms.customerNotes}</div>
                ) : null}
              </div>
            ) : null}

            {expiresAt ? (
              <p className="cpi-summary__meta">Invitation expires {expiresAt}</p>
            ) : null}
          </aside>

          <div className="cpi-form-panel">
            <header className="cpi-form-panel__header">
              <h1>Complete your setup</h1>
              <p>
                Create your Neat Schedule account and add billing to activate your custom plan.
              </p>
            </header>

            {error ? <div className="cpi-alert cpi-alert--error" role="alert">{error}</div> : null}

            <div className="cpi-form-panel__body">
              <section className="cpi-section" aria-labelledby="cpi-account-heading">
                <h2 id="cpi-account-heading" className="cpi-section__title">
                  <span className="cpi-section__step">1</span>
                  Account details
                </h2>

                <div className="cpi-field">
                  <label htmlFor="cpi-email">Email</label>
                  <input
                    id="cpi-email"
                    className="cpi-input"
                    type="email"
                    value={email}
                    readOnly
                  />
                </div>

                <div className="cpi-row">
                  <div className="cpi-field">
                    <label htmlFor="cpi-first-name">First name</label>
                    <input
                      id="cpi-first-name"
                      className="cpi-input"
                      value={firstName}
                      onChange={(event) => onFirstNameChange(event.target.value)}
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="cpi-field">
                    <label htmlFor="cpi-last-name">Last name</label>
                    <input
                      id="cpi-last-name"
                      className="cpi-input"
                      value={lastName}
                      onChange={(event) => onLastNameChange(event.target.value)}
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div className="cpi-field">
                  <label htmlFor="cpi-company">Company name</label>
                  <input
                    id="cpi-company"
                    className="cpi-input"
                    value={companyName}
                    onChange={(event) => onCompanyNameChange(event.target.value)}
                    autoComplete="organization"
                  />
                </div>

                <div className="cpi-field">
                  <label htmlFor="cpi-password">Password</label>
                  <div className="cpi-input-wrap">
                    <input
                      id="cpi-password"
                      className="cpi-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(event) => onPasswordChange(event.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="cpi-toggle-eye"
                      onClick={onTogglePassword}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {password ? (
                    <PasswordMeter
                      score={passwordScore}
                      label={passwordLabel}
                      strengthClass={passwordStrengthClass}
                    />
                  ) : null}
                </div>

                <div className="cpi-field">
                  <label htmlFor="cpi-confirm-password">Confirm password</label>
                  <div className="cpi-input-wrap">
                    <input
                      id="cpi-confirm-password"
                      className="cpi-input"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(event) => onConfirmPasswordChange(event.target.value)}
                      autoComplete="new-password"
                      style={
                        password && confirmPassword && password !== confirmPassword
                          ? { borderColor: '#dc2626' }
                          : undefined
                      }
                    />
                    <button
                      type="button"
                      className="cpi-toggle-eye"
                      onClick={onToggleConfirmPassword}
                    >
                      {showConfirmPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
              </section>

              <section className="cpi-section" aria-labelledby="cpi-billing-heading">
                <h2 id="cpi-billing-heading" className="cpi-section__title">
                  <span className="cpi-section__step">2</span>
                  Billing address
                </h2>

                <div className="cpi-field">
                  <label htmlFor="cpi-street">Street address</label>
                  <input
                    id="cpi-street"
                    className="cpi-input"
                    name="street"
                    value={billingAddress.street}
                    onChange={onBillingChange}
                    autoComplete="street-address"
                  />
                </div>

                <div className="cpi-row cpi-row--3">
                  <div className="cpi-field">
                    <label htmlFor="cpi-city">City</label>
                    <input
                      id="cpi-city"
                      className="cpi-input"
                      name="city"
                      value={billingAddress.city}
                      onChange={onBillingChange}
                      autoComplete="address-level2"
                    />
                  </div>
                  <div className="cpi-field">
                    <label htmlFor="cpi-state">State</label>
                    <input
                      id="cpi-state"
                      className="cpi-input"
                      name="state"
                      value={billingAddress.state}
                      onChange={onBillingChange}
                      autoComplete="address-level1"
                    />
                  </div>
                  <div className="cpi-field">
                    <label htmlFor="cpi-zip">ZIP code</label>
                    <input
                      id="cpi-zip"
                      className="cpi-input"
                      name="zipCode"
                      value={billingAddress.zipCode}
                      onChange={onBillingChange}
                      autoComplete="postal-code"
                    />
                  </div>
                </div>
              </section>

              <section className="cpi-section" aria-labelledby="cpi-payment-heading">
                <h2 id="cpi-payment-heading" className="cpi-section__title">
                  <span className="cpi-section__step">3</span>
                  Payment method
                </h2>

                <div className="cpi-payment-wrap">
                  {payment?.paymentMethodId ? (
                    <p>Card saved{payment.last4 ? ` ending in ${payment.last4}` : ''}.</p>
                  ) : (
                    <DeluxeVaultSetup onVaultReady={onPaymentChange} />
                  )}
                </div>

                <div className="cpi-total-due">
                  <span>Total due today (incl. tax)</span>
                  <strong>${totals.grandTotal.toFixed(2)}</strong>
                </div>
              </section>
            </div>

            <footer className="cpi-form-panel__footer">
              <button
                type="button"
                className="cpi-btn-primary"
                onClick={onAccept}
                disabled={submitDisabled}
              >
                Create account &amp; start subscription
              </button>
              <button type="button" className="cpi-btn-ghost" onClick={onCancel}>
                Cancel
              </button>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
